import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  LinearProgress,
  Stack,
  Typography,
  Alert,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from '@mui/material';
import * as XLSX from '../utils/secureXlsx';
import { importSaleslistRows, SaleslistImportLog, SaleslistImportRowPayload, SaleslistImportRowResult } from '../api/saleslistImportApi';
import { mapImportRow } from '../utils/saleslistImportMapping';

type PreviewRow = SaleslistImportRowPayload & { __sourceIndex: number };

export default function SaleslistImportPage() {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<PreviewRow[]>([]);
  const [parsingError, setParsingError] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const [results, setResults] = useState<SaleslistImportRowResult[] | null>(null);
  const [ignoredMarginColumn, setIgnoredMarginColumn] = useState(false);
  const [unparsedRateCount, setUnparsedRateCount] = useState(0);

  const rowsToImport = useMemo(() => preview.map(({ __sourceIndex, ...p }) => p), [preview]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setParsingError(null);
    setResults(null);
    setIgnoredMarginColumn(false);
    setUnparsedRateCount(0);
    const f = e.target.files?.[0] || null;
    setFile(f);
  };

  const parseExcel = async () => {
    setParsingError(null);
    setPreview([]);
    setIgnoredMarginColumn(false);
    setUnparsedRateCount(0);
    if (!file) return;
    try {
      const wb = await XLSX.readSecureXlsxFile(file);
      const sheetName = wb.SheetNames[0];
      const ws = sheetName ? wb.Sheets[sheetName] : undefined;
      if (!ws) throw new Error('The XLSX workbook contains no worksheet');
      const raw = XLSX.utils.sheet_to_json<Record<string, any>>(ws, { defval: '' });

      let sawIgnoredMarginColumn = false;
      let unparsedRateRowCount = 0;
      const mapped: PreviewRow[] = raw.map((row, idx) => {
        const { row: fields, hasIgnoredMarginColumn, hasUnparsedRate } = mapImportRow(row);
        if (hasIgnoredMarginColumn) sawIgnoredMarginColumn = true;
        if (hasUnparsedRate) unparsedRateRowCount += 1;
        return { __sourceIndex: idx, ...fields } as PreviewRow;
      });

      setPreview(mapped);
      setIgnoredMarginColumn(sawIgnoredMarginColumn);
      setUnparsedRateCount(unparsedRateRowCount);
    } catch (err: any) {
      setParsingError(err?.message ?? 'Failed to parse Excel');
    }
  };

  const startImport = async () => {
    setImporting(true);
    setResults(null);
    try {
      const res = await importSaleslistRows(rowsToImport);
      setResults(res.results);
    } catch (err: any) {
      setParsingError(err?.message ?? 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  return (
    <Box p={3}>
      <Typography variant="h5" gutterBottom>
        Saleslist Import
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Selecteer het Excel-bestand (bijv. "saleslist/Verkooplijst 2025.xlsx") en parseer om een preview te zien. 
        Daarna kun je importeren. Tijdens de import worden acties en meldingen hieronder gelogd.
      </Typography>

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            <Button variant="outlined" component="label">
              Kies Excel (.xlsx)
              <input
                type="file"
                accept={XLSX.XLSX_MIME_TYPE + ',.xlsx'}
                hidden
                onChange={handleFileChange}
              />
            </Button>
            <Typography variant="body2" color="text.secondary">
              {file ? file.name : 'Geen bestand geselecteerd'}
            </Typography>
            <Button onClick={parseExcel} variant="contained" disabled={!file}>Parseer</Button>
            <Button onClick={startImport} variant="contained" color="success" disabled={!preview.length || importing}>
              Importeer {preview.length ? `(${preview.length})` : ''}
            </Button>
          </Stack>
          {importing && (
            <Box mt={2}>
              <LinearProgress />
            </Box>
          )}
          {parsingError && (
            <Box mt={2}>
              <Alert severity="error">{parsingError}</Alert>
            </Box>
          )}
          {ignoredMarginColumn && (
            <Box mt={2}>
              <Alert severity="warning">
                Kolom "marge per uur" wordt genegeerd: de marge wordt altijd berekend door de pricing engine en kan niet via de import worden ingesteld.
              </Alert>
            </Box>
          )}
          {unparsedRateCount > 0 && (
            <Box mt={2}>
              <Alert severity="warning">
                {unparsedRateCount} rij(en) hebben een onleesbaar tarief en worden zonder tarief geïmporteerd.
              </Alert>
            </Box>
          )}
        </CardContent>
      </Card>

      {!!preview.length && (
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6">Preview (eerste 10 rijen)</Typography>
            <Table size="small" sx={{ mt: 1 }}>
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>Titel</TableCell>
                  <TableCell>Bedrijf</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Verkocht door</TableCell>
                  <TableCell>Beroep</TableCell>
                  <TableCell>Startdatum</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {preview.slice(0, 10).map((r, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{r.__sourceIndex + 1}</TableCell>
                    <TableCell>{r.title}</TableCell>
                    <TableCell>{r.companyName}</TableCell>
                    <TableCell>{r.status}</TableCell>
                    <TableCell>{r.verkochtDoor}</TableCell>
                    <TableCell>{r.profession}</TableCell>
                    <TableCell>{r.expectedStartDate ? new Date(r.expectedStartDate).toLocaleDateString() : ''}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {results && (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Import logs
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Stack spacing={1}>
              {results.map((res) => (
                <Box key={res.index} sx={{ p: 1, bgcolor: res.ok ? 'success.50' : 'error.50', borderRadius: 1 }}>
                  <Typography variant="subtitle2">Rij {res.index + 1} — {res.ok ? 'OK' : 'Fout'}</Typography>
                  {res.logs.map((log: SaleslistImportLog, i: number) => (
                    <Typography key={i} variant="body2" color={log.level === 'error' ? 'error.main' : log.level === 'warn' ? 'warning.main' : 'text.secondary'}>
                      [{log.level}] {log.message}
                    </Typography>
                  ))}
                </Box>
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
